import { Button, Sheet, SheetBody, SheetFooter, SheetHeader, TextField } from "@community-project-org/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import type { JSX } from "react";

import { apiRequest, ApiRequestError } from "../../common/api/apiClient.js";
import type { components } from "../../generated/api.js";

type MemberPlanDTO = components["schemas"]["MemberPlanDTO"];
type MemberRuleDTO = components["schemas"]["MemberRuleDTO"];
type ServiceGroupDTO = components["schemas"]["ServiceGroupDTO"];
type ServiceDTO = components["schemas"]["ServiceDTO"];
type QuotaPeriod = components["schemas"]["QuotaPeriod"];

/** Eine Regel im Formular: Ziel als ein Wert, Zahlen als Text, bis gespeichert wird. */
interface RuleDraft {
  target: string;
  discountPercent: string;
  freeQuota: string;
  quotaPeriod: QuotaPeriod;
  earlyAccessDays: string;
}

const EMPTY_RULE: RuleDraft = { target: "", discountPercent: "0", freeQuota: "", quotaPeriod: "MONTH", earlyAccessDays: "0" };

function toDraft(rule: Required<MemberRuleDTO>): RuleDraft {
  return {
    target: rule.groupId ? `g:${rule.groupId}` : rule.serviceId ? `s:${rule.serviceId}` : "",
    discountPercent: String(rule.discountPercent),
    freeQuota: rule.freeQuota === null ? "" : String(rule.freeQuota),
    quotaPeriod: rule.quotaPeriod,
    earlyAccessDays: String(rule.earlyAccessDays),
  };
}

function toRule(draft: RuleDraft): MemberRuleDTO {
  return {
    groupId: draft.target.startsWith("g:") ? draft.target.slice(2) : null,
    serviceId: draft.target.startsWith("s:") ? draft.target.slice(2) : null,
    discountPercent: Number(draft.discountPercent) || 0,
    freeQuota: draft.freeQuota === "" ? null : Number(draft.freeQuota),
    quotaPeriod: draft.quotaPeriod,
    earlyAccessDays: Number(draft.earlyAccessDays) || 0,
  };
}

const selectClass = "min-h-[44px] rounded-md border border-line bg-surface px-2 text-[14px] text-ink";

/**
 * Eine Stufe anlegen oder ändern: Name, Preis, Laufzeit und Regeln. Jede Regel nennt,
 * wofür sie gilt, und was sie bringt — Rabatt, freie Buchungen, Tage Vorverkauf.
 */
export function PlanSheet({
  plan,
  groups,
  services,
  onClose,
}: {
  plan: MemberPlanDTO | null;
  groups: ServiceGroupDTO[];
  services: ServiceDTO[];
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [name, setName] = useState(plan?.name ?? "");
  const [price, setPrice] = useState(plan ? (plan.priceCents / 100).toFixed(2) : "0.00");
  const [days, setDays] = useState(plan?.durationDays ? String(plan.durationDays) : "");
  const [rules, setRules] = useState<RuleDraft[]>(plan ? plan.rules.map(toDraft) : [EMPTY_RULE]);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name,
        priceCents: Math.round(Number(price.replace(",", ".")) * 100),
        durationDays: days === "" ? null : Number(days),
        rules: rules.map(toRule),
      };
      return plan
        ? apiRequest<MemberPlanDTO>(`/members/plans/${encodeURIComponent(plan.id)}`, { method: "PUT", body })
        : apiRequest<MemberPlanDTO>("/members/plans", { method: "POST", body });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["member-plans"] });
      onClose();
    },
  });

  const update = (index: number, patch: Partial<RuleDraft>): void =>
    setRules((current) => current.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)));

  return (
    <Sheet open onClose={onClose} label={plan ? "Stufe bearbeiten" : "Neue Stufe"} size="lg">
      <SheetHeader>
        <h2 className="text-[18px] font-extrabold text-ink">{plan ? plan.name : "Neue Stufe"}</h2>
      </SheetHeader>
      <SheetBody>
        <form
          id="plan-form"
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate();
          }}
        >
          <TextField label="Name" required value={name} onChange={(event) => setName(event.target.value)} />
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Preis je Laufzeit (€)" inputMode="decimal" value={price} onChange={(event) => setPrice(event.target.value)} />
            <TextField
              label="Laufzeit in Tagen"
              inputMode="numeric"
              value={days}
              hint="Leer: unbefristet"
              onChange={(event) => setDays(event.target.value)}
            />
          </div>

          <fieldset className="grid gap-3">
            <legend className="micro pb-1">Regeln</legend>
            {rules.map((rule, index) => (
              <div key={index} className="grid gap-2 rounded border border-line p-3">
                <div className="flex items-end gap-2">
                  <label className="grid flex-1 gap-1 text-[12px] font-bold text-ink-2">
                    Gilt für
                    <select className={selectClass} value={rule.target} onChange={(event) => update(index, { target: event.target.value })}>
                      <option value="">Alle Angebote</option>
                      {groups.map((group) => (
                        <option key={group.id} value={`g:${group.id}`}>
                          Gruppe: {group.name}
                        </option>
                      ))}
                      {services.map((service) => (
                        <option key={service.id} value={`s:${service.id}`}>
                          {service.title}
                        </option>
                      ))}
                    </select>
                  </label>
                  <Button variant="ghost" aria-label="Regel entfernen" onClick={() => setRules(rules.filter((_, i) => i !== index))}>
                    <Trash2 size={16} aria-hidden="true" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <TextField label="Rabatt %" inputMode="numeric" value={rule.discountPercent} onChange={(event) => update(index, { discountPercent: event.target.value })} />
                  <TextField label="Frei je Zeitraum" inputMode="numeric" value={rule.freeQuota} hint="Leer: keine" onChange={(event) => update(index, { freeQuota: event.target.value })} />
                  <label className="grid gap-1 text-[12px] font-bold text-ink-2">
                    Zeitraum
                    <select className={selectClass} value={rule.quotaPeriod} onChange={(event) => update(index, { quotaPeriod: event.target.value as QuotaPeriod })}>
                      <option value="MONTH">Monat</option>
                      <option value="YEAR">Jahr</option>
                      <option value="TOTAL">Laufzeit</option>
                    </select>
                  </label>
                  <TextField label="Vorverkauf (Tage)" inputMode="numeric" value={rule.earlyAccessDays} onChange={(event) => update(index, { earlyAccessDays: event.target.value })} />
                </div>
              </div>
            ))}
            <Button variant="secondary" onClick={() => setRules([...rules, EMPTY_RULE])}>
              <Plus size={16} aria-hidden="true" />
              Regel hinzufügen
            </Button>
          </fieldset>

          {save.error && (
            <p className="text-[13px] text-danger" role="alert">
              {save.error instanceof ApiRequestError ? save.error.message : "Das hat nicht geklappt."}
            </p>
          )}
        </form>
      </SheetBody>
      <SheetFooter>
        <span className="flex-1" />
        <Button variant="secondary" onClick={onClose}>
          Abbrechen
        </Button>
        <Button type="submit" form="plan-form" disabled={save.isPending} aria-busy={save.isPending}>
          {save.isPending ? "Wird gespeichert …" : "Speichern"}
        </Button>
      </SheetFooter>
    </Sheet>
  );
}
